import React, { useCallback, useEffect, useState } from 'react';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	DataTypeEntityOption,
	DataTypeState,
	EntityId,
	EntityType,
	ProjectState,
	VariableDeclarationState,
	toCamelCase,
	READABLE_ENTITY_TYPES,
	EntityError,
	variableDeclarationValidation,
	translateLogicErrorCode,
	lowercaseFirstLetter,
	StateMutationErrorCode,
	EntityInstanceErrorCode,
	translateLogicErrorCodeShort,
	toSentenceCase,
	getRecursiveParentsIds,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import {
	Alert,
	TextField,
	Tooltip as MuiTooltip,
	Checkbox,
	Collapse,
} from '@mui/material';
import CheckboxWithLabel from '../../../lib/checkbox/CheckboxWithLabel';
import { EditorService } from '../../../services/editor';
import DataTypeBuilder, {
	AllowedDataTypeListTypes,
	ILiteralValueOption,
} from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import DynamicValueEntry from '../../dynamic-value/DynamicValueEntry';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import { DataTypeBuilderOption } from '../../../services/editor/utils';
import { Logger } from '@elyx-code/common-ts-utils';
import { debounce } from '../../../utils';

export interface IVariableDeclarationProps {
	project: EditorService;
	entity?: VariableDeclarationState;
	onClose?: (e: any) => void;
}

export enum VariableDeclarationErrorEnum {
	NameRequired = 'name-required',
	IncompleteDataType = 'incomplete-data-type',
}

export type VariableDeclarationError =
	| VariableDeclarationErrorEnum
	| EntityInstanceErrorCode;

export function fromVariableDeclarationErrorToReadableMessage(
	error: VariableDeclarationError | string,
): string {
	switch (error) {
		case VariableDeclarationErrorEnum.NameRequired:
			return 'Name is required';
		case VariableDeclarationErrorEnum.IncompleteDataType:
			return 'The composition of the data type is incomplete. Please fill in all required fields on each data type element.';
		default:
			return toSentenceCase(error);
	}
}

function calculateVariableDeclarationCreationFormErrors(
	entity: VariableDeclarationState,
): (VariableDeclarationError | string)[] {
	const errors: (VariableDeclarationError | string)[] = [];

	if (!entity.name) {
		errors.push(VariableDeclarationErrorEnum.NameRequired);
	}

	if (!!entity.dataType) {
		if (!entity.dataType.isResolved) {
			errors.push(VariableDeclarationErrorEnum.IncompleteDataType);
		}

		const flatErrors = (
			entity.dataType.toFlat().filter((child) => {
				if (child.type === EntityType.DataType) {
					if (child.id === entity.dataType.id) {
						// This is the root data type, skip
						return true;
					}

					const allParentsOfNestedDT = getRecursiveParentsIds(child);

					if (allParentsOfNestedDT.includes(entity.dataType.id)) {
						// This is a nested data type that creates a circular reference
						return true;
					} else {
						return false;
					}
				}

				return false;
			}) as DataTypeState[]
		).flatMap((dt) => dt.getShallowErrors());

		if (flatErrors.length > 0) {
			errors.push(
				...flatErrors.map((err) => translateLogicErrorCodeShort(err)),
			);
		}
	}

	return errors;
}

// Not all entity types can have inputs with any data type
// Sometimes, like in the case of a search, the data type of its inputs can only be a primitive type or a list of primitives
// This function, given an option, will return some metadata that will help it render the option in the data type builder
export function resolveDataTypeOptionConfigForVariableDeclaration(
	option: DataTypeBuilderOption | ILiteralValueOption,
	input: VariableDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsTypeConfigForVariableDeclaration(
	input: VariableDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeLikeConfigForVariableDeclaration(
	input: VariableDeclarationState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsListConfigForVariableDeclaration(
	input: VariableDeclarationState,
): {
	allowed: AllowedDataTypeListTypes;
	tooltip: React.ReactNode;
} {
	return {
		allowed: AllowedDataTypeListTypes.All,
		tooltip: null,
	};
}

const VariableDeclaration: React.FC<IVariableDeclarationProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: VariableDeclarationState | null =
		_entity ||
		(project?.logic?.get(entityId) as VariableDeclarationState | null) ||
		null;

	const [dataType, _setDataType] = useState<DataTypeState | null>(
		entity?.dataType || null,
	);
	const [entityName, setEntityName] = useState<string>(entity.name);
	const [entityNameErrors, setEntityNameErrors] = useState<EntityError[]>(
		variableDeclarationValidation.validateVariableDeclarationName(entity),
	);
	const [entityDescription, setEntityDescription] = useState<string>(
		entity.description,
	);

	const [isConstant, _setIsConstant] = useState<boolean>(entity.constant);
	const [isRequired, _setIsRequired] = useState<boolean>(entity.required);

	const [metadataSectionOpen, setMetadataSectionOpen] = useState(true);
	const [dataTypeSectionOpen, setDataTypeSectionOpen] = useState(false);
	const [defaultValueSectionOpen, setDefaultValueSectionOpen] = useState(false);
	const [interactivitySectionOpen, setInteractivitySectionOpen] =
		useState(false);

	// const setIsRequired = useCallback((value: boolean) => {
	// 	_setIsRequired(value);

	// 	const changeSet = project?.logic?.addChangeSet(
	// 		new ChangeSet(
	// 			project?.logic,
	// 			ProjectState.sessionAuthor,
	// 			new Date().toISOString(),
	// 			entity,
	// 			false,
	// 			StateMutationAction.MakeVarDeclRequired
	// 		),
	// 	);

	// 	entity?.metaSync({ required: value }, changeSet);

	// 	entity?.afterAllChildrenInitialized(changeSet);

	// 	project.renderAndCloseChangeSet(changeSet);
	// }, []);

	const setIsConstant = useCallback((value: boolean) => {
		_setIsConstant(value);

		const changeSet = project?.logic?.addChangeSet(
			new ChangeSet(
				project?.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				entity,
				false,
				value ? StateMutationAction.MakeVarDeclConstant : StateMutationAction.MakeVarDeclNonConstant
			),
		);

		entity?.metaSync({ constant: value }, changeSet);

		entity?.afterAllChildrenInitialized(changeSet);

		project.renderAndCloseChangeSet(changeSet);
	}, []);

	const debouncedOnChangeName = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.RenameVarDecl,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ name: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			1500,
			false,
		),
		[],
	);

	const debouncedOnChangeDescription = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.ChangeVarDeclDescription,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ description: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			600,
			false,
		),
		[],
	);

	const [errors, setErrors] = useState<(VariableDeclarationError | string)[]>(
		calculateVariableDeclarationCreationFormErrors(entity),
	);

	const setDataType = (
		newDataTypeEntity: DataTypeState | null,
		changeSet: ChangeSet | null,
	) => {
		newDataTypeEntity?.setParent(entity, changeSet);
		entity?.setDataType(newDataTypeEntity, changeSet);

		changeSet?.add(entity, ChangeSetEntityChangeType.Affected);

		_setDataType(newDataTypeEntity);
		setUpdateKey((prev) => prev + 1);

		project.renderAndCloseChangeSet(changeSet);
	};

	useEffect(() => {
		const newErrors = calculateVariableDeclarationCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.parent.id, {});
	}, [
		entityName,
		updateKey,
		isConstant,
		isRequired,
		entityName,
		entityDescription,
	]);

	const isFinal =
		// TODO: Improve
		entity.defaultValue?.isFinal || entity.constant;

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: metadataSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Name and description'
								subtitle={`Add a name and description for this ${READABLE_ENTITY_TYPES[
									entity.type
								].singular.toLowerCase()}`}
								hideShowSuffix='the name and description section'
								open={metadataSectionOpen}
								onToggle={() => setMetadataSectionOpen(!metadataSectionOpen)}
							/>

							<Collapse in={metadataSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<TextField
										label='Variable declaration name'
										variant='outlined'
										disabled={!entity.editable}
										style={{ width: '50%' }}
										size='small'
										value={entityName || ''}
										onChange={(e) => {
											Logger.log('Name change: ', e.target.value);
											const validationResult =
												variableDeclarationValidation.validateVariableDeclarationName(
													entity,
													e.target.value,
												);

											setEntityName(e.target.value);

											if (!validationResult.length) {
												debouncedOnChangeName(e.target.value);
											}

											setEntityNameErrors(validationResult);
										}}
										error={!!entityNameErrors.length}
										helperText={
											!!entityNameErrors.length
												? entityNameErrors
														.map((err) => translateLogicErrorCode(err))
														.join('. ')
												: ''
										}
									/>

									<TextField
										label='Variable declaration description'
										variant='outlined'
										disabled={!entity.editable}
										fullWidth
										multiline
										minRows={3}
										size='small'
										value={entityDescription || ''}
										onChange={(e) => {
											setEntityDescription(e.target.value);
											debouncedOnChangeDescription(e.target.value);
										}}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: dataTypeSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Data type'
								subtitle='Select the type of data that this variable declaration'
								hideShowSuffix='the data type section'
								open={dataTypeSectionOpen}
								onToggle={() => setDataTypeSectionOpen(!dataTypeSectionOpen)}
								headerStartIcon={
									<EntityIcon entity={entity} project={project} />
								}
							/>

							<Collapse in={dataTypeSectionOpen}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DataTypeBuilder
										project={project}
										topParent={entity}
										dataType={dataType}
										readOnly={!entity.editable}
										onChangeSet={() => {
											const newChangeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeVarDeclDataType,
												),
											);

											return newChangeSet;
										}}
										onChange={(newDataTypeEntity, changeSet) => {
											setDataType(newDataTypeEntity, changeSet);
										}}
										lists={
											resolveDataTypeAsListConfigForVariableDeclaration(entity)
												.allowed
										}
										getOptionDisabled={(option) =>
											resolveDataTypeOptionConfigForVariableDeclaration(
												option,
												entity,
											).disabled
										}
										getListDisabledTooltip={(option) =>
											resolveDataTypeAsListConfigForVariableDeclaration(entity)
												.tooltip
										}
										getOptionTooltip={(option) =>
											resolveDataTypeOptionConfigForVariableDeclaration(
												option,
												entity,
											).tooltip
										}
										disallowAsType={
											resolveDataTypeAsTypeConfigForVariableDeclaration(entity)
												.disabled
										}
										getAsTypeDisabledTooltip={() =>
											resolveDataTypeAsTypeConfigForVariableDeclaration(entity)
												.tooltip
										}
										disallowLike={
											resolveDataTypeLikeConfigForVariableDeclaration(entity)
												.disabled
										}
										getLikeDisabledTooltip={() =>
											resolveDataTypeLikeConfigForVariableDeclaration(entity)
												.tooltip
										}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: defaultValueSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title={isFinal ? 'Value' : 'Default value'}
								subtitle={
									isFinal
										? 'The value of this variable declaration'
										: 'The initial value, if no other is given when the variable declarationsed'
								}
								hideShowSuffix={
									isFinal ? 'the value section' : 'the default value section'
								}
								open={defaultValueSectionOpen}
								onToggle={() =>
									setDefaultValueSectionOpen(!defaultValueSectionOpen)
								}
							/>

							<Collapse in={defaultValueSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DynamicValueEntry
										entity={entity}
										project={project}
										parentDisabledReasons={
											!entity.editable
												? [StateMutationErrorCode.ActionOwnerEntityNotEditable]
												: []
										}
										onlyDefault
									>
										{(extraContent) => extraContent}
									</DynamicValueEntry>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: interactivitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Interactivity'
								subtitle='Define how this variable declaration the logic'
								hideShowSuffix='the interactivity section'
								open={interactivitySectionOpen}
								onToggle={() =>
									setInteractivitySectionOpen(!interactivitySectionOpen)
								}
							/>

							<Collapse in={interactivitySectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'row',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									{/* This is from when the 'required' field meant "nullable" */}
									{/* <MuiTooltip
										title={
											'When checked, the value of this variable declaration cannot be set to empty'
										}
										placement='bottom'
										arrow
										enterDelay={false ? 100 : 400}
										leaveDelay={200}
									>
										<FormGroup>
											<FormControlLabel
												control={
													<Checkbox
														disabled={!entity.editable}
														className='variable-declaration-builder--required-checkbox'
														checked={isRequired}
														onChange={(e) => {
															const value = e.target.checked;
															setIsRequired(value);
														}}
													/>
												}
												label={'Required'}
											/>
										</FormGroup>
									</MuiTooltip> */}

									<MuiTooltip
										title={
											'Whether the logic can edit an existing value, or the first value set is the only value'
										}
										placement='bottom'
										arrow
										enterDelay={false ? 100 : 400}
										leaveDelay={200}
									>
										<CheckboxWithLabel
											mainLabel="Constant value"
											subLabel={isConstant ? "Value is constant and cannot be edited" : "Value can be edited"}
											disabled={!entity.editable}
											className='variable-declaration-builder--required-checkbox'
											checked={isConstant}
											onChange={(checked) => {
												setIsConstant(checked as boolean);
											}}
										/>
									</MuiTooltip>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromVariableDeclarationErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromVariableDeclarationErrorToReadableMessage(
															error,
														)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='variable-declaration--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#variable-declaration--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? `Resolve the errors to save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`
								: `Save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default VariableDeclaration;
